import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Filter, Search, X } from 'lucide-react';
import { Input } from '../../ui/input';
import { cn } from '../../../lib/utils';
import { SegButton, Segmented } from './Segmented';
import { todayISO } from './trackerView';
import {
  channelBars, channelDot, CUSTOM_RANGE, entryName, formatLoggedAt, isBackdated, LOG_RANGES, loggedBy, LONG_NOTE_CHARS, nextContactCell, rangeText,
} from './logView';

const PILL_TONES = {
  negative: 'border-negative/40 bg-negative/15 text-negative-light',
  warning: 'border-warning/30 bg-warning/10 text-warning',
  info: 'border-brand-blue/40 bg-brand-blue/10 text-brand-blue',
  done: 'border-white/10 bg-surface-2 text-ink-dim',
  none: 'border-dashed border-white/15 bg-transparent text-ink-dim',
};

function Tile({ label, children }) {
  return (
    <div className="rounded-xl border border-white/10 bg-surface-1 px-[18px] py-4">
      <div className="text-xs font-bold uppercase tracking-wider text-ink-dim">{label}</div>
      {children}
    </div>
  );
}

function TileValue({ value, sub, className }) {
  return (
    <div className="mt-2 flex items-baseline gap-2.5">
      <span className={cn('text-[30px] font-bold tabular-nums', className || 'text-ink')}>{value ?? 0}</span>
      <span className="text-[13px] text-ink-dim">{sub}</span>
    </div>
  );
}

// LogTiles: counts over every contact matching the filters (not only this page).
export function LogTiles({ summary, filters }) {
  const bars = channelBars(summary);
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3">
      <Tile label="Contacts logged"><TileValue value={summary?.contacts} sub={rangeText(filters)} /></Tile>
      <Tile label="Players reached"><TileValue value={summary?.players} sub="unique" /></Tile>
      <Tile label="Follow-ups overdue">
        <TileValue value={summary?.overdue} sub="from these contacts" className={summary?.overdue ? 'text-negative-light' : undefined} />
      </Tile>
      <Tile label="By channel">
        <div className="mt-3.5 flex h-2 overflow-hidden rounded bg-surface-2">
          {bars.map((b) => <div key={b.label} className={cn('h-2', b.dot)} style={{ width: `${b.pct}%` }} />)}
        </div>
        <div className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink-dim">
          {bars.length === 0 && <span>No contacts</span>}
          {bars.map((b) => (
            <span key={b.label} className="inline-flex items-center gap-1.5">
              <span className={cn('h-2 w-2 rounded-sm', b.dot)} />{b.label} <span className="font-bold text-ink">{b.n}</span>
            </span>
          ))}
        </div>
      </Tile>
    </div>
  );
}

// DateRange: the custom From-To days (inclusive; either may be left open).
function DateRange({ from, to, onFilters }) {
  const today = todayISO();
  return (
    <div role="group" aria-label="Custom date range" className="flex items-center gap-2 rounded-xl border border-white/10 bg-surface-1 p-1 pl-3">
      <span className="text-xs text-ink-dim">From</span>
      <Input
        type="date" aria-label="From" value={from} max={to || today}
        className="h-10 w-auto [color-scheme:dark]" onChange={(e) => onFilters({ from: e.target.value })}
      />
      <span className="text-xs text-ink-dim">to</span>
      <Input
        type="date" aria-label="To" value={to} min={from || undefined} max={today}
        className="h-10 w-auto [color-scheme:dark]" onChange={(e) => onFilters({ to: e.target.value })}
      />
    </div>
  );
}

// LogToolbar: whose contacts, date range, channel, the player filter and search.
export function LogToolbar({ filters, agent, channels, playerName, search, onFilters, onSearch }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Segmented label="Whose contacts">
        <SegButton on={filters.scope !== 'all'} onClick={() => onFilters({ scope: 'agent' })}>{agent || 'This manager'}</SegButton>
        <SegButton on={filters.scope === 'all'} onClick={() => onFilters({ scope: 'all' })}>All managers</SegButton>
      </Segmented>
      <Segmented label="Date range">
        {LOG_RANGES.map((r) => (
          <SegButton key={r.key} on={filters.days === r.key} onClick={() => onFilters({ days: r.key })}>{r.label}</SegButton>
        ))}
      </Segmented>
      {filters.days === CUSTOM_RANGE && <DateRange from={filters.from} to={filters.to} onFilters={onFilters} />}
      <Segmented label="Channel">
        <SegButton on={!filters.channel} onClick={() => onFilters({ channel: 0 })}>All</SegButton>
        {channels.map((c) => (
          <SegButton key={c.channel_id} on={filters.channel === c.channel_id} onClick={() => onFilters({ channel: c.channel_id })}>
            {c.channel}
          </SegButton>
        ))}
      </Segmented>
      {filters.player > 0 && (
        <div className="flex min-h-10 items-center gap-1.5 rounded-full border border-brand-blue/40 bg-brand-blue/10 pl-3.5 pr-1.5 text-[13px] font-bold text-brand-blue">
          Player: {playerName}
          <button
            type="button" aria-label="Clear player filter" onClick={() => onFilters({ player: 0 })}
            className="grid h-8 w-8 place-items-center rounded-full hover:bg-brand-blue/15"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      <div className="relative ml-auto w-full min-w-[280px] sm:w-auto">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-dim" />
        <Input
          type="search" value={search} onChange={(e) => onSearch(e.target.value)} maxLength={200}
          placeholder="Player, email, ID or note text" aria-label="Search contacts" className="h-11 pl-10"
        />
      </div>
    </div>
  );
}

const COLUMNS = 'grid grid-cols-[minmax(200px,1fr)_120px_minmax(320px,2.4fr)_150px_170px] gap-5 px-5';

function Note({ entry }) {
  const [open, setOpen] = useState(false);
  const long = (entry.note || '').length > LONG_NOTE_CHARS;
  return (
    <div className="min-w-0">
      {entry.note
        ? <p className={cn('whitespace-pre-wrap text-sm leading-relaxed text-ink', long && !open && 'line-clamp-2')}>{entry.note}</p>
        : <span className="text-sm text-ink-mute">No note</span>}
      {long && (
        <button
          type="button" aria-expanded={open} onClick={() => setOpen(!open)}
          className="mt-1.5 min-h-7 text-[13px] font-bold text-brand-blue hover:underline"
        >
          {open ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

function Entry({ entry, onPlayer }) {
  const next = nextContactCell(entry);
  return (
    <div className={cn(COLUMNS, 'items-start border-t border-white/5 py-4 transition-colors hover:bg-surface-1')}>
      <div className="min-w-0">
        <div className="flex items-center gap-1">
          <Link
            to={`/player/${entry.user_id}?from=tracker`} title="Open the player profile"
            className="truncate text-left text-[15px] font-bold text-ink hover:text-cta hover:underline"
          >
            {entryName(entry)}
          </Link>
          <button
            type="button" onClick={() => onPlayer(entry.user_id)}
            aria-label={`Show only ${entryName(entry)}'s contacts`} title="Show only this player's contacts"
            className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-ink-dim hover:bg-surface-2 hover:text-ink"
          >
            <Filter className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="mt-0.5 truncate text-[12.5px] text-ink-dim">{entry.user_id}{entry.email ? ` · ${entry.email}` : ''}</div>
      </div>
      <div>
        <span className="inline-flex h-[26px] items-center gap-1.5 rounded-full border border-white/10 bg-surface-2 px-2.5 text-[12.5px] font-bold text-ink">
          <span className={cn('h-[7px] w-[7px] rounded-full', channelDot(entry.channel))} />{entry.channel}
        </span>
      </div>
      <Note entry={entry} />
      <div className="flex flex-col items-center text-center">
        <span className={cn('inline-flex h-[26px] items-center whitespace-nowrap rounded-full border px-2.5 text-xs font-bold', PILL_TONES[next.tone])}>{next.label}</span>
        {next.sub && <div className="mt-1 text-xs text-ink-dim">{next.sub}</div>}
      </div>
      <div className="min-w-0">
        <div className={cn('truncate text-sm font-bold', entry.vip_agent ? 'text-ink' : 'text-ink-dim')}>{entry.vip_agent || 'Unassigned'}</div>
        <div className="truncate text-xs text-ink-dim" title={entry.manager_email}>by {loggedBy(entry.manager_email)}</div>
        <div
          className={cn('mt-0.5 whitespace-nowrap text-xs', isBackdated(entry) ? 'text-warning' : 'text-ink-dim')}
          title={isBackdated(entry) ? 'Logged on a later day than the contact' : undefined}
        >
          logged {formatLoggedAt(entry.created_at)}
        </div>
      </div>
    </div>
  );
}

// LogTable: the page's contacts grouped by contact day. A player's name opens their profile; the
// filter icon beside it shows only their contacts.
export function LogTable({ groups, onPlayer }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-white/10 bg-surface">
      <div className="min-w-[1100px]">
        <div className={cn(COLUMNS, 'py-3 text-xs font-bold tracking-wide text-ink-dim')}>
          <div>Player</div><div>Channel</div><div>Note</div><div className="text-center">Next contact</div><div>Manager · logged</div>
        </div>
        {groups.map((g) => (
          <div key={g.day}>
            <div className="flex items-center gap-2.5 border-t border-white/10 bg-surface-1/60 px-5 py-2.5 text-[13px]">
              <span className="font-bold text-ink">{g.label}</span><span className="text-ink-dim">{g.sub}</span>
            </div>
            {g.items.map((e) => <Entry key={e.contact_id} entry={e} onPlayer={onPlayer} />)}
          </div>
        ))}
      </div>
    </div>
  );
}
